import React, { useCallback, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import { Editor, Range } from "slate";
import { Box } from "@mui/system";
import { useWysiwygEditor } from "../useWysiwygEditor";

interface ControlPanelProps {
  editor: Editor;
}

const normalizeUrl = (url: string): string => {
  const trimmed = url.trim();
  if (!trimmed) {
    return trimmed;
  }
  return /^(https?:\/\/|mailto:|tel:)/i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
};

export const ControlPanel = ({ editor }: ControlPanelProps) => {
  const theme = useTheme();
  const {
    isMarkActive,
    isBlockActive,
    toggleMark,
    toggleBlock,
    isLinkActive,
    getActiveLinkUrl,
    upsertLink,
    removeLink,
  } = useWysiwygEditor({ editor });

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrlInput, setLinkUrlInput] = useState("");
  const [savedSelection, setSavedSelection] = useState<Range | null>(null);
  const [editingExistingLink, setEditingExistingLink] = useState(false);

  const onBoldClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      toggleMark("bold");
    },
    [toggleMark]
  );

  const onItalicClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      toggleMark("italic");
    },
    [toggleMark]
  );

  const onUnderlineClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      toggleMark("underline");
    },
    [toggleMark]
  );

  const onNumberedListClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      toggleBlock("numberedList");
    },
    [toggleBlock]
  );

  const onBulletedListClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      toggleBlock("bulletedList");
    },
    [toggleBlock]
  );

  // Capture the editor selection on mousedown, before focus moves to the toolbar
  // button (and later to the dialog input), so we can restore it when applying.
  const onLinkMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      const existingUrl = getActiveLinkUrl(editor.selection);
      setSavedSelection(editor.selection);
      setEditingExistingLink(Boolean(existingUrl));
      setLinkUrlInput(existingUrl ?? "");
    },
    [editor, getActiveLinkUrl]
  );

  const onLinkClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      setLinkDialogOpen(true);
    },
    []
  );

  const closeDialog = useCallback(() => {
    setLinkDialogOpen(false);
    setLinkUrlInput("");
    setSavedSelection(null);
    setEditingExistingLink(false);
  }, []);

  const onApplyLink = useCallback(() => {
    const url = normalizeUrl(linkUrlInput);
    if (url) {
      upsertLink(url, savedSelection);
    }
    closeDialog();
  }, [linkUrlInput, savedSelection, upsertLink, closeDialog]);

  const onRemoveLink = useCallback(() => {
    removeLink(savedSelection);
    closeDialog();
  }, [savedSelection, removeLink, closeDialog]);

  return (
    <>
      <ToggleButtonGroup
        value={Editor.marks(editor)}
        size={"small"}
        sx={{ backgroundColor: "transparent" }}
      >
        <ToggleButton
          value="bold"
          selected={isMarkActive("bold")}
          onClick={onBoldClicked}
        >
          <FormatBoldIcon />
        </ToggleButton>
        <ToggleButton
          value="italic"
          selected={isMarkActive("italic")}
          onClick={onItalicClicked}
        >
          <FormatItalicIcon />
        </ToggleButton>
        <ToggleButton
          value="underline"
          selected={isMarkActive("underline")}
          onClick={onUnderlineClicked}
        >
          <FormatUnderlinedIcon />
        </ToggleButton>
        <ToggleButton
          value="link"
          selected={isLinkActive()}
          onMouseDown={onLinkMouseDown}
          onClick={onLinkClicked}
        >
          <InsertLinkIcon />
        </ToggleButton>
        <Box borderLeft={`1px solid ${theme.newColors.neutral.gray200}`} />
        <ToggleButton
          value="numberedList"
          selected={isBlockActive("numberedList")}
          onClick={onNumberedListClicked}
        >
          <FormatListNumberedIcon />
        </ToggleButton>
        <ToggleButton
          value="bulletedList"
          selected={isBlockActive("bulletedList")}
          onClick={onBulletedListClicked}
        >
          <FormatListBulletedIcon />
        </ToggleButton>
      </ToggleButtonGroup>
      <Dialog
        open={linkDialogOpen}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 2 }}>
          {editingExistingLink ? "Edit link" : "Add link"}
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 3, px: 3, overflow: "visible" }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="URL"
            placeholder="https://example.com"
            value={linkUrlInput}
            sx={{ mt: 2 }}
            onChange={(e) => setLinkUrlInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onApplyLink();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ pt: 0, pb: 2, px: 3 }}>
          {editingExistingLink && (
            <Button color="error" onClick={onRemoveLink}>
              Remove
            </Button>
          )}
          <Box flexGrow={1} />
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={onApplyLink}
            disabled={!linkUrlInput.trim()}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
